import { DynamicMappingDataQuery } from "@agenturid/u20tools";

export class QuestionDataHelper {

  constructor(qctrl) {
    this.qctrl = qctrl;
  }


  getOptionParentFieldName() {
    return 'parentItem';
  }

  getChildOptionsForOption(option, alloptions) {
    const l = [];
    for (const o of alloptions) {
      if (o[this.getOptionParentFieldName()]) {
        const parentId = o[this.getOptionParentFieldName()]._id;
        if (
          option._id === parentId ||
          (option.ref_orig && option.ref_orig === parentId)
        ) {
          o.children = this.getChildOptionsForOption(o, alloptions);
          l.push(o);
        }
      }
    }
    return l;
  }

  getOptionsHierarchical(question, alloptions) {
    const rootOptions = alloptions.filter(
      (m) =>
        typeof m[this.getOptionParentFieldName()] === 'undefined' ||
        m[this.getOptionParentFieldName()] == null ||
        typeof m[this.getOptionParentFieldName()]._id === 'undefined'
    );
    for (const o of rootOptions) {
      o.children = this.getChildOptionsForOption(o, alloptions);
    }
    return rootOptions;
  }

  /**
   * Adds missing parent options to a filtered list.
   * This is important when dynamic queries return only child elements -
   * we need to also include their parent elements for proper hierarchical display.
   * 
   * @param {Array} filteredOptions - The options returned by dynamic query
   * @param {Array} allOptions - All available options (including parents)
   * @returns {Array} - Options list with all necessary parent elements added
   */
  addMissingParentOptions(filteredOptions, allOptions) {
    const parentFieldName = this.getOptionParentFieldName();
    
    // Create a map for quick lookup of all options by ID
    const allOptionsMap = new Map();
    for (const opt of allOptions) {
      const id = opt._id || opt.id;
      if (id) {
        allOptionsMap.set(id, opt);
      }
    }
    
    // Create a set of IDs already in the filtered list
    const filteredIds = new Set();
    for (const opt of filteredOptions) {
      const id = opt._id || opt.id;
      if (id) {
        filteredIds.add(id);
      }
    }
    
    // Find all parent options that need to be added
    const parentsToAdd = [];
    
    const findAndAddParents = (option) => {
      if (!option || !option[parentFieldName]) return;
      
      const parentRef = option[parentFieldName];
      const parentId = parentRef._id || parentRef.id || parentRef;
      
      if (!parentId) return;
      
      // Check if parent is already in filtered list or already queued to add
      if (filteredIds.has(parentId)) return;
      if (parentsToAdd.some(p => (p._id || p.id) === parentId)) return;
      
      // Find parent in all options
      const parentOption = allOptionsMap.get(parentId);
      if (parentOption) {
        parentsToAdd.push(parentOption);
        filteredIds.add(parentId); // Mark as added to prevent duplicates
        
        // Recursively find grandparents
        findAndAddParents(parentOption);
      }
    };
    
    // Process all filtered options to find their missing parents
    for (const opt of filteredOptions) {
      findAndAddParents(opt);
    }
    
    // Return combined list: parents first, then original filtered options
    // This ensures parents appear before their children in the flat list
    return [...parentsToAdd, ...filteredOptions];
  }

  filterDynamicOptions(question, options, hierarchically=false) {
    let o = options;
    // Keep reference to all original options for parent lookup
    const allOriginalOptions = [...options];

    if (question.dynamicDataMapping) {
      if (
        Array.isArray(question.dynamicDataMapping) &&
        question.dynamicDataMapping.length > 0
      ) {
        const w = qctrl.getWebsite();
        const list = [];
        for (const d of question.dynamicDataMapping) {
          try {
            if (d.query) {
              const q = new DynamicMappingDataQuery(w);
              if (d.query.query) {
                let l = q.filterList(o, d.query.query, d.query.select.id);
                l = q.sortElements(l, d.query.sort);
                list.push(...l);
              }
              else {
                let l = d.query.sort ? q.sortElements(o, d.query.sort) : o;
                list.push(...l);
              }

            } else {
              list.push(...o);
            }
          } catch (e) {
            console.error(e);
          }
        }
        o = list;
        
        // For hierarchical questions: Add missing parent elements
        // This ensures parent options are displayed even when the dynamic query
        // only returns child elements
        if (hierarchically && question.hierarchical === true) {
          o = this.addMissingParentOptions(o, allOriginalOptions);
        }
      }
    }

    if (hierarchically) {
      o = this.getOptionsHierarchical(question, o);
    }

    return o;
  }

  loadOptionsFromQuestion(question, hierarchically=false) {
    const options = question['options'];

    let o = options.filter((m) => {
      if (m.id) {
        return m.id != null;
      }
      if (m._id) {
        return m._id != null;
      }

      return true;
    });

    o = this.filterDynamicOptions(question, o, hierarchically);

    return o;
  }

}